import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import '@/assets/css/responsive.css';
import { withSentryReactRouterV6Routing } from '@sentry/react';
import { useInteractionState } from '@/hooks';
import { MainPageComponent, ShopCartComponent } from '@/modules/components';
import { createClient } from '@neondatabase/neon-js';
import { MenuItem } from '@/type';
import { each } from 'lodash';
import DataCenter from '@/modules/data/DataCenter';

const client = createClient({
  auth: {
    url: import.meta.env.VITE_NEON_AUTH_URL,
    allowAnonymous: true,
  },
  dataApi: {
    url: import.meta.env.VITE_NEON_DATA_API_URL,
  },
});

const Content = () => {
  const {
    orderEnable,
    setOrderEnable,
    setManagement,
    setMenus,
    setProducts,
    setCategories,
    setWorkHours,
  } = useInteractionState();

  useEffect(() => {
    const queryData = async () => {
      const queryManagementData = () => {
        return client
          .from('management')
          .select(
            `
                announcement_de,
                announcement_en,
                announcement_zn,
                email_disable,
                company:company!company_id(name,branch,description,address,city,postal,country,telephone,email,latitude,longitude),
                menuDocument:document!menu_document_id(*),
                mediaDocument:document!media_document_id(*)
              `
          )
          .eq('state', 'ACTIVE')
          .order('created_at', { ascending: false })
          .limit(1);
      };

      const queryMenuData = () => {
        return client.from('menu').select('*').order('sequence');
      };

      const queryProductData = () => {
        return client
          .from('product')
          .select(
            `
                *, 
                document!document_id(*),
                category!product_to_category(*)
              `
          )
          .eq('state', 'ACTIVE')
          .order('sequence');
      };

      const queryCategoryData = async () => {
        return client
          .from('category')
          .select('*')
          .eq('show', 'true')
          .order('sequence');
      };

      const queryWorkHourData = async () => {
        return client.from('work_hours').select('*').order('id');
      };

      const [
        managementData,
        productsData,
        menusData,
        categoryData,
        workHourData,
      ] = await Promise.all([
        queryManagementData(),
        queryProductData(),
        queryMenuData(),
        queryCategoryData(),
        queryWorkHourData(),
      ]);
      setManagement?.(() => {
        const mgmt = managementData.data?.[0];
        if (!mgmt) return undefined;
        return {
          ...mgmt,
          company: Array.isArray(mgmt.company) ? mgmt.company[0] : mgmt.company,
          menuDocument: Array.isArray(mgmt.menuDocument)
            ? mgmt.menuDocument[0]
            : mgmt.menuDocument,
          mediaDocument: Array.isArray(mgmt.mediaDocument)
            ? mgmt.mediaDocument[0]
            : mgmt.mediaDocument,
        };
      });
      setProducts?.(productsData.data ?? []);
      setMenus?.(() => {
        each(menusData.data ?? [], (it: MenuItem) => {
          it.navigateToMenu = it.show && it.key === DataCenter.LUNCH_MENU;
          it.navigateToCart =
            it.show &&
            it.key === DataCenter.CART_MENU &&
            it.key !== DataCenter.HOME_MENU;
          it.target = it.show && it.key === DataCenter.HOME_MENU ? '/' : '#';
          if (it.navigateToCart) {
            setOrderEnable?.(true);
          }
        });
        return menusData.data ?? [];
      });

      setCategories?.(() => {
        if (categoryData.data && categoryData.data.length > 0) {
          categoryData.data[0].selected = true;
        }
        return categoryData.data ?? [];
      });

      setWorkHours?.(workHourData.data ?? []);
    };
    queryData();
  }, [
    setCategories,
    setManagement,
    setMenus,
    setOrderEnable,
    setProducts,
    setWorkHours,
  ]);

  const SentryRoutes = withSentryReactRouterV6Routing(Routes);

  return (
    <BrowserRouter>
      <SentryRoutes>
        <Route path="/" element={<MainPageComponent />} />
        {orderEnable && <Route path="/cart" element={<ShopCartComponent />} />}
        <Route path="*" element={<Navigate to="/" />} />
      </SentryRoutes>
    </BrowserRouter>
  );
};

export default Content;
